import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useApiService} from "../context/ApiServiceContext";
import {ContentDraftStore} from "./contentDraftStore";

// Public read side of a revisioned content document: defaults render on the very
// first paint, a valid preview draft short-circuits the API entirely, and the
// published copy merges in when it arrives.

export interface RevisionedContentConfig<T> {
    url: string;
    defaults: T;
    // Merges an untrusted payload over the defaults - section-whole, never field
    // by field, so a bad payload can never blank a section.
    mergeContent: (incoming: any) => T;
    draftStore: ContentDraftStore<T>;
    previewParam?: string;
    // False when an ancestor provider already loaded this document (see
    // SiteContentProvider) - the hook then renders defaults without fetching.
    enabled?: boolean;
}

export interface RevisionedContentState<T> {
    content: T;
    isPreview: boolean;
}

const useRevisionedContent = <T>(config: RevisionedContentConfig<T>): RevisionedContentState<T> => {
    const {url, defaults, mergeContent, draftStore, previewParam = 'preview', enabled = true} = config;

    const apiService = useApiService();
    // HashRouter: window.location.search is EMPTY for /#/?preview=x - the query
    // lives inside the hash, and only the router's location exposes it.
    const location = useLocation();

    const previewToken = useMemo(
        () => new URLSearchParams(location.search).get(previewParam),
        [location.search, previewParam]
    );

    // Read synchronously during render so a preview never flashes the published copy.
    const draft = useMemo(
        () => (previewToken && enabled ? draftStore.read(previewToken) : null),
        [previewToken, draftStore, enabled]
    );

    const [fetched, setFetched] = useState<any>(null);

    useEffect(() => {
        // A valid draft short-circuits the API entirely.
        if (draft || !enabled) {
            return;
        }

        let cancelled = false;
        // The public read stays cacheable on purpose (Cache-Control: max-age=60) and
        // goes through `contentApi`, which points at the CDN in production and carries
        // no auth interceptor - see the comment on it in ApiService. A failure here is
        // not fatal: the hook keeps rendering the defaults.
        apiService.contentApi.get<{ content: any }>(url)
            .then(response => {
                if (!cancelled) {
                    setFetched(response.data?.content ?? null);
                }
            })
            .catch(error => {
                console.error(`Fetching ${url} failed`, error);
            });

        return () => {
            cancelled = true;
        };
    }, [apiService, draft, url, enabled]);

    useEffect(() => {
        if (previewToken && enabled) {
            draftStore.purgeExpired();
        }
    }, [previewToken, draftStore, enabled]);

    // Defaults render on the very first paint; fetched/draft content merges in later.
    const content = useMemo(() => {
        const incoming = draft ?? fetched;
        return incoming ? mergeContent(incoming) : defaults;
    }, [draft, fetched, mergeContent, defaults]);

    return {content, isPreview: !!draft};
};

export default useRevisionedContent;
